import { AspectRatio } from '@/components/ui/atoms/aspect-ratio'
import EmptyList from '@/components/ui/atoms/empty-list'
import Pagination from '@/components/ui/molecules/pagination'
import type { ICursor } from '@/types/cursor'
import type { INews } from '@/types/news'
import { useCallback, useEffect, useRef } from 'react'

export default function NewsList({
  region,
  tags: _ssr_tags,
  nodes: news,
  cursor,
  pathname,
  search,
}: {
  region?: string
  tags?: Array<string>
  nodes: Array<INews>
  cursor: ICursor
  pathname: string
  search?: string
}) {
  const newsRef = useRef<HTMLElement | null>(null)

  const handleScroll = useCallback(() => {
    if (newsRef.current) {
      newsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

      const offset = 180
      const elementPosition =
        newsRef.current?.getBoundingClientRect().top + window.scrollY
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }, [])

  useEffect(() => {
    if (search?.length || Number(cursor?.page || 1) > 1) {
      handleScroll()
    }
  }, [])

  return (
    <>
      <section className="mt-8" ref={newsRef}>
        <h2 className="text-2xl font-bold tracking-tight text-zinc-700 sm:text-3xl">
          {search
            ? `Notícias relacionadas a: "${search}"`
            : region
              ? `Concursos ${region}`
              : 'Notícias da semana'}
        </h2>
      </section>

      {news.length === 0 && <EmptyList pathname={pathname} />}
      <div className="mx-auto mb-10 grid grid-cols-1 gap-x-8 gap-y-16 sm:pt-7 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        {news.map((item) => (
          <article
            key={item.slug}
            className="flex max-w-xl flex-col items-start justify-between self-baseline"
          >
            <div
              className={
                'relative rounded-xl my-3 sm:mb-0 w-full h-full clip-path-inset border border-zinc-300'
              }
            >
              <a href={item.href}>
                <AspectRatio ratio={16 / 9}>
                  <img
                    loading="lazy"
                    src={item.media || '/assets/images/no-article-image.png'}
                    width="0"
                    height="0"
                    sizes="100%"
                    className="rounded-xl w-full h-full object-cover bg-gradient-to-tr from-zinc-300"
                    alt="Banner do artigos dos professores"
                  />
                </AspectRatio>
              </a>
            </div>
            <div className="group relative">
              {item.tags && item.tags?.length > 0 && (
                <p className="font-medium tracking-wider text-xs mt-3 uppercase text-sky-600">
                  {item?.tags[0].name}
                </p>
              )}
              <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600">
                <a href={item.href}>
                  <span className="absolute inset-0" />
                  {item.title}
                </a>
              </h3>
              <div
                className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: item.text?.html || '',
                }}
              />
            </div>
            <div className="relative mt-4 flex items-center justify-between gap-x-4">
              <img
                loading="lazy"
                src={item?.author?.avatarUrl || ''}
                alt={item?.author?.name || `Autor - ${item.title}`}
                className="h-10 w-10 rounded-full bg-gradient-to-tr from-zinc-300"
                width={40}
                height={40}
              />
              <div className="text-sm leading-6">
                <a
                  href={`/autor/${item?.author?.slug}`}
                  className="font-semibold text-gray-900 hover:text-sky-600 hover:underline block"
                >
                  {item?.author?.name}
                </a>
                <time dateTime={item.date.d} className="text-gray-500">
                  {item.date.dl}
                </time>
              </div>
            </div>
          </article>
        ))}
      </div>
      {news?.length &&
      cursor?.total &&
      cursor?.page &&
      cursor?.pages &&
      cursor?.pages > 1 ? (
        <Pagination
          total={cursor?.total}
          page={cursor?.page}
          pages={cursor?.pages}
          object={'notícia'}
          pathname={pathname}
          query={search ? `?search=${search}` : ''}
        />
      ) : (
        <></>
      )}
    </>
  )
}
